// Add Kid modal — parent flow for adding a kid profile to their
// household.
//
// Kids are local profiles under the household's shared kid-device login
// (set up once via "Shared kids login" in household settings). There is
// no per-kid Auth0 account. A PIN is optional: set one when the kid
// shares the tablet with siblings and you want their account gated in
// the picker; leave it off and they're tappable straight through.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api } from "../../lib/rpc";
import { AvatarPicker } from "../../components/avatar-picker";

interface AddKidModalProps {
  open: boolean;
  householdId: number;
  onClose: () => void;
  onCreated: (kidId: string) => void;
  notify: (msg: string) => void;
}

export function AddKidModal({ open, householdId, onClose, onCreated, notify }: AddKidModalProps) {
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  // PIN is collapsed by default — only expand when the parent wants the
  // picker to gate this kid behind a code.
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [avatar, setAvatar] = useState<{ color: string | null; emoji: string | null }>({ color: null, emoji: null });
  const [saving, setSaving] = useState(false);

  // Reset state each time the modal (re)opens so a previous cancel
  // doesn't leak a half-filled form on the next open.
  useEffect(() => {
    if (!open) return;
    setName("");
    setAge("");
    setShowPin(false);
    setPin("");
    setPinConfirm("");
    setAvatar({ color: null, emoji: null });
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  // PIN section, if enabled, requires a 4-6 digit code and a confirm match.
  const pinSectionValid = !showPin || (/^\d{4,6}$/.test(pin) && pin === pinConfirm);
  const canSave = name.trim().length > 0 && pinSectionValid && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const result = await api.householdsAddKid(householdId, {
        displayName: name.trim(),
        pin: showPin ? pin : undefined,
        avatarColor: avatar.color,
        avatarEmoji: avatar.emoji,
        age: age.trim() ? Number(age) : null,
      });
      notify(`Added ${result.displayName}`);
      onCreated(result.kidId);
      onClose();
    } catch (err) {
      notify((err as Error)?.message || "couldn't add kid");
      setSaving(false);
    }
  };

  return (
    <div className="confirm-bg" onClick={() => { if (!saving) onClose(); }}>
      <div className="add-kid-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Add a kid">
        <header className="add-kid-head">
          <h3>Add a kid</h3>
          <button className="icon-btn" onClick={onClose} title="Close" type="button" disabled={saving}>
            <Icon name="x" size={13} />
          </button>
        </header>

        <p className="add-kid-sub">
          Kids sign in on the family tablet with your shared kids login, then pick
          their own profile. Add a PIN if you want this kid's profile gated behind a
          code — otherwise they just tap their avatar to get in.
        </p>

        <div className="add-kid-body">
          <div className="add-kid-row">
            <label className="add-kid-label">Name</label>
            <input
              className="add-kid-input"
              value={name}
              onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
              maxLength={40}
              placeholder="e.g. Ezra"
              autoFocus
            />
          </div>

          <div className="add-kid-row">
            <label className="add-kid-label">Age</label>
            <input
              className="add-kid-input add-kid-input-narrow"
              value={age}
              onInput={(e) => setAge((e.currentTarget as HTMLInputElement).value)}
              type="number"
              min="0"
              max="99"
              placeholder="9"
            />
          </div>

          <div className="add-kid-row">
            <label className="add-kid-label">Avatar</label>
            <AvatarPicker value={avatar} onChange={setAvatar} size="full" />
          </div>

          {/* Optional PIN — collapsed by default. */}
          <div className="add-kid-row">
            {!showPin ? (
              <button
                className="manage-tags-done"
                type="button"
                onClick={() => setShowPin(true)}
                style={{ alignSelf: "flex-start" }}
              >
                <Icon name="plus" size={11} /> Add a PIN (optional)
              </button>
            ) : (
              <>
                <label className="add-kid-label">
                  PIN
                  <button
                    type="button"
                    onClick={() => { setShowPin(false); setPin(""); setPinConfirm(""); }}
                    style={{ marginLeft: 8, background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 11 }}
                  >
                    Skip
                  </button>
                </label>
                <div className="add-kid-pin-pair">
                  <input
                    className="add-kid-input add-kid-input-narrow"
                    value={pin}
                    onInput={(e) => setPin((e.currentTarget as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6))}
                    type="password"
                    inputMode="numeric"
                    pattern="\d*"
                    placeholder="4-6 digits"
                    autoComplete="new-password"
                  />
                  <input
                    className="add-kid-input add-kid-input-narrow"
                    value={pinConfirm}
                    onInput={(e) => setPinConfirm((e.currentTarget as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6))}
                    type="password"
                    inputMode="numeric"
                    pattern="\d*"
                    placeholder="Confirm"
                    autoComplete="new-password"
                  />
                </div>
                {pin && !/^\d{4,6}$/.test(pin) && (
                  <div className="add-kid-hint add-kid-hint-warn">PIN must be 4-6 digits</div>
                )}
                {pin && /^\d{4,6}$/.test(pin) && pin !== pinConfirm && (
                  <div className="add-kid-hint add-kid-hint-warn">PINs don't match</div>
                )}
              </>
            )}
          </div>
        </div>

        <footer className="add-kid-foot">
          <button className="manage-tags-done" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="manage-tags-add-btn" type="button" onClick={submit} disabled={!canSave}>
            {saving ? "Saving…" : "Add kid"}
          </button>
        </footer>
      </div>
    </div>
  );
}
